import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen pt-20 pb-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-[#404040] mb-4">
            Get In Touch
          </h1>
          <p className="text-lg text-gray-600">
            We're here to help. Contact us today to discuss your property needs
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-none shadow-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-[#FF8C42] p-3 rounded-lg">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#404040] mb-2">Visit Us</h3>
                  <p className="text-gray-600 text-sm">
                    25 Masons Avenue<br />
                    Harrow HA3 5AH<br />
                    United Kingdom
                  </p>
                </div>
              </div>
            </Card>

            <Card className="border-none shadow-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-[#FF8C42] p-3 rounded-lg">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#404040] mb-2">Call Us</h3>
                  <p className="text-gray-600 text-sm">
                    020 8189 8889
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Mon-Fri: 9AM - 6PM
                  </p>
                </div>
              </div>
            </Card>

            <Card className="border-none shadow-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-[#FF8C42] p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#404040] mb-2">Email Us</h3>
                  <p className="text-gray-600 text-sm">
                    info@harryandjacobs.com
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    We'll respond within 24 hours
                  </p>
                </div>
              </div>
            </Card>

            <Card className="border-none shadow-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-[#FF8C42] p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#404040] mb-2">Opening Hours</h3>
                  <div className="text-gray-600 text-sm space-y-1">
                    <p>Mon-Fri: 9:00 AM - 6:00 PM</p>
                    <p>Saturday: 10:00 AM - 4:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#404040] mb-6">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#404040] font-medium mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#404040] font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#404040] font-medium mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="020 1234 5678"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-[#404040] font-medium mb-2 block">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Property Enquiry"
                        required
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#404040] font-medium mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help you..."
                      rows={6}
                      required
                      className="w-full resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg py-6 btn-hover-lift"
                  >
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-12">
          <Card className="border-none shadow-lg overflow-hidden">
            <div className="h-96 bg-gray-200 relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.3089!2d-0.3346!3d51.5908!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDM1JzI3LjAiTiAwwrAyMCcwNC42Ilc!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Harry & Jacobs Location"
              ></iframe>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
